import { IDriverUser, IMedList, IUserBase } from '../../types/type';
import './adminmedlist.css';
import { useState, useMemo, useEffect } from 'react';
import { normalizeImageUrl } from '../../utils/utils';
import { formatBackendShortDate, formatBackendTime } from '../../utils/date.utils';
import AvatarImageCard from '../AvatarImageCard/AvatarImageCard';
import { MedicalService, MedListFilters } from '../../services/medical.service';
import Table, {
  LabeledCell,
  LabeledSecondaryCell,
  ColumnConfig,
  ExpandedRowConfig,
  SecondaryRowConfig,
} from '../Table/Table';
import StatusCell from '../Table/StatusCell';
import Pagination from '../Pagination/Pagination';

function AdminMedList({ user }: { user: IDriverUser | IUserBase }) {
  const [medLists, setMedLists] = useState<IMedList[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Состояние для фильтров
  const [filters, setFilters] = useState<MedListFilters>({
    page: 1,
    limit: 10,
    startTime: '',
    endTime: '',
    fullName: '',
    num: '',
    checkStatus: '',
    autoparkName: '',
  });

  // Состояние для пагинации
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  });

  // Статус-маппинг для визуализации статусов
  const statusMap = {
    completed: { label: 'Осмотр пройден', dotClass: 'active' },
    rejected: { label: 'Осмотр не пройден', dotClass: 'rejected' },
    waiting: { label: 'Ожидает', dotClass: 'waiting' },
    inProgress: { label: 'В процессе', dotClass: 'inProgress' },
    canceled: { label: 'Отменен', dotClass: 'canceled' },
  };

  // Подсчет статистики медицинских осмотров
  const medStats = useMemo(() => {
    const totalCount = medLists?.length || 0;
    const positiveCount = medLists?.filter((med) => med.status === 'completed').length || 0;
    const negativeCount = medLists?.filter((med) => med.status === 'rejected').length || 0;

    return {
      totalCount,
      positiveCount,
      negativeCount,
    };
  }, [medLists]);

  // Функция для обновления фильтров
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Функция для сброса фильтров
  const resetFilters = () => {
    setFilters({
      page: 1,
      limit: 10,
      startTime: '',
      endTime: '',
      fullName: '',
      num: '',
      checkStatus: '',
      autoparkName: '',
    });
  };

  // Функция для изменения страницы
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters((prev) => ({ ...prev, page: newPage }));
    }
  };

  // Конфигурация колонок для таблицы
  const tableColumns: ColumnConfig[] = [
    {
      id: 'date',
      title: 'Дата',
      render: (row) => (
        <>
          <div className="table-cell-title">Дата</div>
          <div className="table-cell-value">{formatBackendShortDate(row.checkTime)}</div>
        </>
      ),
    },
    {
      id: 'time',
      title: 'Время',
      render: (row) => (
        <>
          <div className="table-cell-title">Время</div>
          <div className="table-cell-value">{formatBackendTime(row.checkTime)}</div>
        </>
      ),
    },
    {
      id: 'num',
      title: 'Номер заявки',
      render: (row) => (
        <>
          <div className="table-cell-title">Номер заявки</div>
          <div className="table-cell-value">{row.num}</div>
        </>
      ),
    },
    {
      id: 'status',
      title: 'Статус',
      render: (row) => {
        const status = row.status === 'completed' ? 'completed' : row.status === 'rejected' ? 'rejected' : 'waiting';
        console.log('row.status: ', row.status);
        return <StatusCell status={row.status} statusMap={statusMap} />;
      },
      className: 'table-cell-centered',
    },
    {
      id: 'actions',
      title: 'Смотреть',
      render: () => null, // Рендеринг происходит внутри компонента Table
    },
  ];

  // Конфигурация вторичных строк (всегда видимых)
  const secondaryRows: SecondaryRowConfig[] = [
    {
      id: 'autopark',
      condition: (row) => !!row.autopark,
      content: (row) => (
        <LabeledSecondaryCell title="Автопарк" value={row.autopark.legalName || 'Не указан'} colSpan={5} />
      ),
    },
  ];

  // Конфигурация расширяемых строк
  const expandedRows: ExpandedRowConfig[] = [
    {
      id: 'parameters',
      content: (row) => (
        <>
          <LabeledCell title="Температура" value={row.bodyTemperature} />
          <LabeledCell title="Давление" value={row.bloodPressure} />
          <LabeledCell title="Пульс" value={row.heartRate} />
          <LabeledCell title="Насыщение O2" value={row.saturation || '-'} colSpan={2} />
        </>
      ),
    },
    {
      id: 'conclusion',
      content: (row) => (
        <>
          <LabeledCell title="Алкотестер" value={`${row.bloodAlcohol} pm`} />
          <LabeledCell title="Жалобы" value={row.symptomsPresent ? 'Есть' : 'Нет'} />
          <LabeledCell
            title="Заключение"
            value={
              row.status === 'completed' ? 'Положительное' : row.status === 'rejected' ? 'Отрицательное' : 'Ожидает'
            }
            colSpan={2}
          />
        </>
      ),
    },
    {
      id: 'refusal',
      content: (row) =>
        row.reasonForRefusal ? (
          <td colSpan={5}>
            <div className="reason-section">
              <div className="reason-title">Причина отказа:</div>
              <div className="reason-text">{row.reasonForRefusal}</div>
            </div>
          </td>
        ) : null,
    },
    {
      id: 'photos',
      content: (row) =>
        row.selfie ? (
          <td colSpan={5}>
            <div className="photo-section">
              <div className="photo-title">Фото:</div>
              <img src={normalizeImageUrl(row.selfie)} alt="Фото водителя" className="driver-photo" />
            </div>
          </td>
        ) : null,
    },
  ];

  // Загрузка данных с учетом фильтров
  const fetchMedLists = async () => {
    if (!user || !user.id) return;

    setLoading(true);
    setError(null);

    try {
      const medicID = user.id;
      const response = await MedicalService.getMedicMedLists(medicID, filters);

      setMedLists(response.data);
      setPagination({
        currentPage: response.page,
        totalPages: response.totalPages,
        totalCount: response.totalCount,
      });
    } catch (err: any) {
      console.error('Ошибка при загрузке медицинских осмотров:', err);
      setError(err.message || 'Не удалось получить списки медосмотров');

      // Сбрасываем данные при ошибке
      setMedLists([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Вызываем загрузку данных при изменении фильтров или пользователя
  useEffect(() => {
    fetchMedLists();
  }, [filters, user]);

  return (
    <div className={`adminMedlist ${user.role === 'medic' || user.role === 'driver' ? 'adminMedlistMedic' : ''}`}>
      {user.role !== 'medic' && user.role !== 'driver' && <AvatarImageCard user={user} />}
      <div className="infoCard">
        {/* Блок фильтров */}
        <div className="medFiltersContainer">
          <div className="medFiltersRow">
            <div className="medFilterItem">
              <label>Начало периода</label>
              <input
                type="date"
                name="startTime"
                value={filters.startTime || ''}
                onChange={handleFilterChange}
                placeholder="DD-MM-YYYY"
              />
            </div>
            <div className="medFilterItem">
              <label>Конец периода</label>
              <input
                type="date"
                name="endTime"
                value={filters.endTime || ''}
                onChange={handleFilterChange}
                placeholder="DD-MM-YYYY"
              />
            </div>
            <div className="medFilterItem">
              <label>Результат осмотра</label>
              <select name="checkStatus" value={filters.checkStatus || ''} onChange={handleFilterChange}>
                <option value="">Все</option>
                <option value="completed">Осмотр пройден</option>
                <option value="rejected">Осмотр не пройден</option>
                <option value="waiting">Ожидает</option>
              </select>
            </div>
          </div>
          <div className="medFiltersRow">
            <div className="medFilterItem">
              <label>Автопарк</label>
              <input
                type="text"
                name="autoparkName"
                value={filters.autoparkName || ''}
                onChange={handleFilterChange}
                placeholder="Название автопарка"
              />
            </div>
            <div className="medFilterItem">
              <label>Номер заявки</label>
              <input
                type="text"
                name="num"
                value={filters.num || ''}
                onChange={handleFilterChange}
                placeholder="Номер заявки"
              />
            </div>
            <div className="medFilterItem">
              <button className="resetFiltersBtn" onClick={resetFilters}>
                Сбросить фильтры
              </button>
            </div>
          </div>
        </div>

        {/* Блок статистики медицинских осмотров */}
        <div
          className="medStatsContainer"
          style={{
            padding: '10px 25px',
          }}
        >
          <div className="medStatsText" style={{ fontWeight: 'bold', color: '#333' }}>
            Осмотров: {medStats.totalCount}, из них Положительных {medStats.positiveCount}, Отрицательных{' '}
            {medStats.negativeCount}
          </div>
        </div>

        {loading ? (
          <div className="loadingIndicator">Загрузка...</div>
        ) : error ? (
          <div className="errorMessage">{error}</div>
        ) : (
          <>
            <div className="admListContainer">
              <Table
                data={medLists}
                columns={tableColumns}
                expandedRows={expandedRows}
                secondaryRows={secondaryRows}
                keyField="id"
                noDataMessage="Нет данных для отображения"
              />
            </div>

            {/* Пагинация */}
            {pagination.totalPages > 1 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminMedList;
